import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import PhotoGrid, { Photo } from '@/components/PhotoGrid';
import PhotoDetail from '@/components/PhotoDetail';
import { useTranslation } from 'react-i18next';

// Image host base URL (new host stores absolute URLs in config)
const API_BASE_URL = 'https://cdn-bun.stardots.io/swainphoto';
const resolvePhotoUrl = (path: string) => path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
// Dummy photos for fallback
const dummyPhotos: Photo[] = [
  {
    id: '1',
    title: '暮色山川',
    description: '夕阳西下，山峦起伏如海浪般层层叠叠，光影交织出大自然的壮丽画卷。',
    url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '风景',
    createdAt: '2023-05-15T12:00:00Z'
  },
  {
    id: '2',
    title: '城市几何',
    description: '现代城市建筑的几何线条与结构之美，展现了人类智慧与创造力的结晶。',
    url: 'https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1486718448742-163732cd1544?auto=format&fit=crop&w=800',
    width: 800,
    height: 1200,
    category: '建筑',
    createdAt: '2023-06-22T15:30:00Z'
  },
  {
    id: '3',
    title: '海洋律动',
    description: '大海有着永不停歇的律动，波涛汹涌处是生命与力量的交响曲。',
    url: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?auto=format&fit=crop&w=800',
    width: 1200,
    height: 900,
    category: '自然',
    createdAt: '2023-07-08T09:15:00Z'
  },
  {
    id: '4',
    title: '晨雾山林',
    description: '清晨的薄雾笼罩着山林，如梦似幻的景色让人仿佛置身仙境。',
    url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '风景',
    createdAt: '2023-08-14T06:45:00Z'
  },
  {
    id: '5',
    title: '玻璃与钢铁',
    description: '现代建筑中玻璃与钢铁的完美结合，反射着城市的多彩光影。',
    url: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '建筑',
    createdAt: '2023-09-03T14:20:00Z'
  },
  {
    id: '6',
    title: '花与光',
    description: '花朵在阳光的照耀下展现出生命的活力与色彩的绚烂。',
    url: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=800',
    width: 1200,
    height: 900,
    category: '自然',
    createdAt: '2023-10-11T11:05:00Z'
  },
  {
    id: '7',
    title: '雪山之巅',
    description: '巍峨的雪山屹立在云端，象征着自然的壮美与庄严。',
    url: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1682687982501-1e58ab814714?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '风景',
    createdAt: '2023-11-25T16:40:00Z'
  },
  {
    id: '8',
    title: '都市夜景',
    description: '夜幕下的城市灯火璀璨，勾勒出现代都市的繁华与活力。',
    url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200',
    thumbnailUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800',
    width: 1200,
    height: 800,
    category: '城市',
    createdAt: '2023-12-07T20:30:00Z'
  }
];

const Gallery = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/photos.json`);
        const data = await response.json();
        
        // Transform data to match Photo interface
        const transformedPhotos = data.map((photo: any): Photo => ({
          id: photo.id,
          title: photo.title,
          description: photo.description,
          url: resolvePhotoUrl(photo.full_url),
          thumbnailUrl: resolvePhotoUrl(photo.preview_url),
          width: photo.width,
          height: photo.height,
          category: photo.category !== 'uncategorized' ? photo.category : undefined,
          createdAt: photo.date || new Date().toISOString()
        }));
        
        setPhotos(transformedPhotos);
        
        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(transformedPhotos.map((photo: Photo) => photo.category).filter(Boolean))
        ) as string[];
        
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching photos:', error);
        // Fallback to dummy data
        setPhotos(dummyPhotos);
        
        const uniqueCategories = Array.from(
          new Set(dummyPhotos.map(photo => photo.category).filter(Boolean))
        ) as string[];
        
        setCategories(uniqueCategories);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPhotos();
  }, []);
  
  const filteredPhotos = selectedCategory 
    ? photos.filter(photo => photo.category === selectedCategory)
    : photos;
    
  const currentPhotoIndex = id ? photos.findIndex(photo => photo.id === id) : -1;
  const currentPhoto = currentPhotoIndex !== -1 ? photos[currentPhotoIndex] : null;
  const nextPhotoId = currentPhotoIndex < photos.length - 1 ? photos[currentPhotoIndex + 1]?.id : undefined;
  const prevPhotoId = currentPhotoIndex > 0 ? photos[currentPhotoIndex - 1]?.id : undefined;
  
  const handleClosePhotoDetail = () => {
    navigate('/gallery');
  };

  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      <NavBar />
      
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-playfair mb-8 text-center">{t('home.footer.gallery')}</h1>
          
          {/* Categories */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
            <button
              className={`px-4 py-2 text-sm border ${
                selectedCategory === null 
                  ? 'border-primary bg-primary text-primary-foreground' 
                  : 'border-border hover:bg-secondary/50 transition-colors'
              }`}
              onClick={() => setSelectedCategory(null)}
            >
              {t('gallery.all')}
            </button>
            
            {categories.map(category => (
              <button
                key={category}
                className={`px-4 py-2 text-sm border ${
                  selectedCategory === category
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border hover:bg-secondary/50 transition-colors'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {t(`gallery.${category}`)}
              </button>
            ))}
          </div>
          
          {/* Photo Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(index => (
                <div key={index} className="aspect-square bg-gray-200 rounded-md" />
              ))}
            </div>
          ) : (
            <PhotoGrid photos={filteredPhotos} />
          )}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="py-12 bg-photo-dark text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-2xl font-playfair tracking-wider mb-4 md:mb-0">
              Swain Photography
            </div>
            
            <nav className="flex items-center space-x-8">
              <a href="/" className="text-sm uppercase tracking-wider hover:text-photo-accent transition-colors">
                {t('home.footer.portfolio')}
              </a>
              <a href="/gallery" className="text-sm uppercase tracking-wider hover:text-photo-accent transition-colors">
                {t('home.footer.galllery')}
              </a>
            </nav>
          </div>
          
          <div className="border-t border-white/20 mt-8 pt-8 text-center md:text-left text-sm text-white/60">
            {t('home.footer.copyright')}
          </div>
        </div>
      </footer>
      
      {/* Photo Detail Modal */}
      {currentPhoto && (
        <PhotoDetail
          photo={currentPhoto}
          nextPhotoId={nextPhotoId}
          prevPhotoId={prevPhotoId}
          onClose={handleClosePhotoDetail}
        />
      )}
    </div>
  );
};

export default Gallery;
